import { useState } from "react";
import { toast } from "sonner";
import { useReviewModeration } from "@/hooks/useReviews";
import { formatApiError } from "@/lib/api";

const onError = (fallback) => (e) => toast.error(formatApiError(e.response?.data?.detail) || fallback);

export function useReviewAdmin() {
  const m = useReviewModeration();
  const [pendingDelete, setPendingDelete] = useState(null);
  const [replyTarget, setReplyTarget] = useState(null);

  const setStatus = (review, status) =>
    m.setStatus.mutate(
      { id: review.id, status },
      {
        onSuccess: () => toast.success(status === "published" ? "Review approved — now visible to customers" : "Review hidden from the storefront"),
        onError: onError("Could not update review"),
      }
    );

  const confirmDelete = () => {
    if (!pendingDelete) return;
    m.remove.mutate(
      { id: pendingDelete.id, handle: pendingDelete.product_id },
      { onSuccess: () => toast.success("Review deleted"), onError: onError("Could not delete review") }
    );
    setPendingDelete(null);
  };

  const saveReply = (body) => {
    if (!replyTarget) return;
    m.reply.mutate(
      { id: replyTarget.id, body },
      { onSuccess: () => { toast.success("Reply published"); setReplyTarget(null); }, onError: onError("Could not save reply") }
    );
  };

  const removeReply = () => {
    if (!replyTarget) return;
    m.removeReply.mutate(
      { id: replyTarget.id },
      { onSuccess: () => { toast.success("Reply removed"); setReplyTarget(null); }, onError: onError("Could not remove reply") }
    );
  };

  return {
    pendingDelete,
    setPendingDelete,
    replyTarget,
    setReplyTarget,
    setStatus,
    confirmDelete,
    saveReply,
    removeReply,
    isDeleting: m.remove.isPending,
    isSavingReply: m.reply.isPending || m.removeReply.isPending,
    isUpdating: m.setStatus.isPending,
  };
}
